import { AttendanceCustomTimeRangeDayEnumValues } from 'generated/resources/datatable.graphql'
import {
  AttendanceCustomTimeRangeDayEnum,
  AttendanceEnumsPoolTypeEnum,
  GetCustomTimeRangesDocument,
  GetCustomTimeRangesQuery,
  Maybe,
  TimeSettingsEnumsTimeTypeEnum,
} from 'generated/resources/graphql'

import { useLazyQuery, useQuery } from 'modules/core/hooks/useQuery'
import { dateBackendDateTimeToUITimeRange } from 'modules/core/lib/dates'
import { minutesToText } from 'modules/core/lib/duration'

type AttendanceCustomTimeRange =
  GetCustomTimeRangesQuery['attendance']['customTimeRangesConnection']['nodes'][number]

export type RangeDayType = {
  [key: string]: CustomTimeRange[]
}

export type GroupedRangeType = {
  [day in AttendanceCustomTimeRangeDayEnum]: RangeDayType
}

export const useFetchCustomTimeRanges = (settingId?: number) => {
  const {
    data: ranges,
    loading,
    refetch,
  } = useQuery(GetCustomTimeRangesDocument, {
    variables: {
      attendanceEmployeesSettingId: settingId,
    },
    fetchPolicy: 'cache-first',
    skip: !settingId,
  })

  return {
    loading,
    ranges: new CustomTimeRangesCollection(
      ranges?.attendance.customTimeRangesConnection.nodes || []
    ),
    refetch,
  }
}

export const useLazyFetchCustomTimeRanges = () => {
  const [fetchCustomTimeRanges, { loading }] = useLazyQuery(GetCustomTimeRangesDocument)

  const fetch = async (settingId: number) => {
    const { data } = await fetchCustomTimeRanges({
      variables: {
        attendanceEmployeesSettingId: settingId,
      },
    })

    const ranges = new CustomTimeRangesCollection(
      data?.attendance.customTimeRangesConnection.nodes || []
    )

    return ranges
  }

  return { loading, fetch }
}

export class CustomTimeRangesCollection {
  static base = 'base'
  static custom = 'custom'

  customTimeRanges: CustomTimeRange[]
  constructor(customTimeRanges: AttendanceCustomTimeRange[]) {
    this.customTimeRanges = customTimeRanges.map((range) => new CustomTimeRange(range))
  }

  get baseTypeTimeRanges(): CustomTimeRange[] {
    return this.customTimeRanges.filter((range) => range.isBase)
  }

  get customTypeTimeRanges(): CustomTimeRange[] {
    return this.customTimeRanges.filter((range) => range.isCustom)
  }

  get hasOvertimeTimeOffCustomTimeRanges(): boolean {
    return !!this.customTimeRanges.find((range) => range.isOvertime && range.isPoolTypeTimeOff)
  }

  get hasCustomTimeRanges(): boolean {
    return this.customTimeRanges.length > 0
  }

  // I need a shape like this for the table
  // {
  //   custom: {
  //     weekday: { "09:00 - 10:00": [] },
  //     bank_holiday: { "09:00 - 10:00": [] },
  //     saturday: { "09:00 - 10:00": [] },
  //     sunday: { "09:00 - 10:00": [] }
  //   },
  //   base: {
  //     weekday: { "no_range": [] },
  //     bank_holiday: { "no_range": [] },
  //     saturday: { "no_range": [] },
  //     sunday: { "no_range": [] }
  //   }
  // }

  groupedTimeRanges(ranges: CustomTimeRange[]): GroupedRangeType {
    const grouped = ranges.reduce<GroupedRangeType>((hash, range) => {
      const { day, rangeKey, startTime } = range
      if (day === null || day === undefined) return hash
      hash[day] = hash[day] || {}

      hash[day]![rangeKey] = hash[day]![rangeKey] || []
      hash[day]![rangeKey]!.push(range)
      if (startTime) {
        hash[day]![rangeKey] = this.sortTimeRangesByStartTime(hash[day]![rangeKey]!)
      }

      return hash
    }, this.dayEnumsHash)

    return grouped
  }

  get groupedBaseTimeRanges(): GroupedRangeType {
    const baseTimeRanges: GroupedRangeType = this.groupedTimeRanges(this.baseTypeTimeRanges)

    return baseTimeRanges
  }

  get groupedCustomTimeRanges(): GroupedRangeType {
    const customTypeTimeRanges: GroupedRangeType = this.groupedTimeRanges(this.customTypeTimeRanges)

    return customTypeTimeRanges
  }

  rangesInSpecificBaseDay(day: AttendanceCustomTimeRangeDayEnum): CustomTimeRange[] {
    return this.filterByDay(this.baseTypeTimeRanges, day)
  }

  rangesInSpecificCustomDay(day: AttendanceCustomTimeRangeDayEnum): CustomTimeRange[] {
    return this.sortTimeRangesByStartTime(this.filterByDay(this.customTypeTimeRanges, day))
  }

  filterByDay(ranges: CustomTimeRange[], day: AttendanceCustomTimeRangeDayEnum) {
    return ranges.filter((range) => range.day === day)
  }

  hasRangesInSpecificBaseDay(day: AttendanceCustomTimeRangeDayEnum): boolean {
    return this.rangesInSpecificBaseDay(day).length > 0
  }

  hasRangesInSpecificCustomDay(day: AttendanceCustomTimeRangeDayEnum): boolean {
    return this.rangesInSpecificCustomDay(day).length > 0
  }

  sortTimeRangesByStartTime(ranges: CustomTimeRange[]) {
    return ranges.sort((a, b) => a.startTime.localeCompare(b.startTime))
  }

  get dayEnumsHash(): GroupedRangeType {
    return AttendanceCustomTimeRangeDayEnumValues.reduce((acc, day) => {
      acc[day] = acc[day] || {}
      return acc
    }, {} as GroupedRangeType)
  }

  get payrollOrTimeOffPools() {
    return this.customTimeRanges.some((range) => range.isPoolTypeTimeOff || range.isPoolTypePayroll)
  }

  get hasPayrollCompensations() {
    return this.customTimeRanges.some((range) => range.isPoolTypePayroll)
  }

  get hasTimeoffCompensations() {
    return this.customTimeRanges.some((range) => range.isPoolTypeTimeOff)
  }
}

export class CustomTimeRange {
  static base = 'base'
  static custom = 'custom'

  id: number
  name: string
  day: AttendanceCustomTimeRangeDayEnum | null | undefined
  timeType: TimeSettingsEnumsTimeTypeEnum
  poolType: AttendanceEnumsPoolTypeEnum
  attendanceCustomTimeRangeCategoryId?: Maybe<number>
  timeSettingsCustomTimeRangeCategoryId: number
  attendanceEmployeesSettingId: number
  rawMinutesInCents: number
  equivalentMinutesInCents: number
  startTime: string
  endTime: string

  constructor(timeRange: AttendanceCustomTimeRange) {
    this.id = timeRange.id
    this.name = timeRange.name
    this.day = timeRange.day
    this.poolType = timeRange.poolType
    this.timeType = timeRange.timeType
    this.attendanceCustomTimeRangeCategoryId = timeRange.attendanceCustomTimeRangeCategoryId
    this.timeSettingsCustomTimeRangeCategoryId = timeRange.timeSettingsCustomTimeRangeCategoryId
    this.attendanceEmployeesSettingId = timeRange.attendanceEmployeesSettingId
    this.rawMinutesInCents = timeRange.rawMinutesInCents
    this.equivalentMinutesInCents = timeRange.equivalentMinutesInCents
    this.startTime = timeRange.startTime
    this.endTime = timeRange.endTime
  }

  get isCustom() {
    return this.startTime && this.endTime
  }

  get isBase() {
    return !this.isCustom
  }

  get type() {
    return this.isBase ? CustomTimeRange.base : CustomTimeRange.custom
  }

  get isOvertime() {
    return this.timeType === 'overtime'
  }

  get isRegular() {
    return this.timeType === 'regular'
  }

  get isPoolTypeBalance() {
    return this.poolType === 'balance'
  }

  get isPoolTypeTimeOff() {
    return this.poolType === 'virtual_timeoff'
  }

  get isPoolTypePayroll() {
    return this.poolType === 'virtual_payroll'
  }

  get rangeKey() {
    return this.isCustom
      ? dateBackendDateTimeToUITimeRange(this.startTime, this.endTime)
      : 'no_range'
  }

  get rawMinutes() {
    return minutesToText(this.rawMinutesInCents / 100)
  }

  get equivalentMinutes() {
    return minutesToText(this.equivalentMinutesInCents / 100)
  }

  findMatchingRange<
    T extends Pick<
      AttendanceCustomTimeRange,
      'timeSettingsCustomTimeRangeCategoryId' | 'day' | 'startTime' | 'endTime' | 'poolType'
    >,
  >(newCustomTimeRanges: T[]): T | undefined {
    return newCustomTimeRanges.find(
      (newCustomTimeRange) =>
        newCustomTimeRange.timeSettingsCustomTimeRangeCategoryId ===
          this.timeSettingsCustomTimeRangeCategoryId &&
        newCustomTimeRange.day === this.day &&
        newCustomTimeRange.startTime === this.startTime &&
        newCustomTimeRange.endTime === this.endTime &&
        newCustomTimeRange.poolType === this.poolType
    )
  }
}
